import React, { memo, useCallback } from 'react';
import { Handle, Position } from 'reactflow';
import { Paper, Typography, Box, alpha, Chip } from '@mui/material';
import { Api } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const APINode = memo(({ data, selected, id }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Node color - Use theme info colors
  const nodeColor = theme.palette.info.main;
  const textColor = theme.palette.info.contrastText;
  const borderColor = selected ? theme.palette.secondary.main : 'transparent';

  const handleDoubleClick = useCallback((event) => {
    event.stopPropagation();
    const editEvent = new CustomEvent('editNode', { detail: { nodeId: id } });
    window.dispatchEvent(editEvent);
  }, [id]);

  const handleContextMenu = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    const contextEvent = new CustomEvent('nodeContextMenu', {
      detail: {
        nodeId: id,
        x: event.clientX,
        y: event.clientY
      }
    });
    window.dispatchEvent(contextEvent);
  }, [id]);

  const getMethodColor = (method) => {
    switch(method?.toUpperCase()) {
      case 'GET': return theme.palette.success.main;
      case 'POST': return theme.palette.warning.main;
      case 'PUT': return theme.palette.info.main;
      case 'DELETE': return theme.palette.error.main;
      case 'PATCH': return theme.palette.secondary.main;
      default: return theme.palette.text.disabled;
    }
  };

  return (
    <Paper
      elevation={selected ? 12 : 4}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      sx={{
        padding: 2.5,
        borderRadius: 3,
        minWidth: 200,
        minHeight: 100,
        position: 'relative',
        overflow: 'visible',
        cursor: 'move',
        background: isDark
          ? `linear-gradient(145deg, ${alpha('#0D2E4D', 0.9)}, ${alpha('#0D2E4D', 0.8)})`
          : `linear-gradient(145deg, ${alpha(nodeColor, 0.95)}, ${alpha(nodeColor, 0.85)})`,
        backdropFilter: 'blur(10px)',
        border: selected
          ? `2px solid ${theme.palette.secondary.main}`
          : `1px solid ${alpha(nodeColor, 0.3)}`,
        transition: theme.transitions.create([
          'transform', 'box-shadow', 'border'
        ], {
          duration: theme.transitions.duration.shorter,
        }),
        '&:hover': {
          transform: 'translateY(-4px) scale(1.02)',
          boxShadow: theme.shadows[16],
          border: `1px solid ${alpha(theme.palette.secondary.main, 0.5)}`,
          '& .api-icon': {
            transform: 'rotate(360deg)',
          }
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: 'inherit',
          background: `radial-gradient(circle at top right, ${alpha('#f2c526', 0.1)}, transparent)`,
          pointerEvents: 'none',
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`API node: ${data.label || 'API Call'}`}
      aria-selected={selected}
    >
      {/* Handles */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: `linear-gradient(135deg, ${nodeColor} 0%, #1976d2 100%)`,
          border: `2px solid ${theme.palette.background.paper}`,
          width: 14,
          height: 14,
          boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.2)}`,
        }}
      />

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          p: 1.5,
          mx: -1,
          mt: -1,
          borderRadius: 2,
          background: alpha(nodeColor, 0.15),
          backdropFilter: 'blur(8px)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            p: 0.5,
            borderRadius: 1.5,
            background: `linear-gradient(135deg, ${nodeColor} 0%, #1976d2 100%)`,
            display: 'flex',
            color: 'white',
            boxShadow: `0 2px 8px ${alpha(nodeColor, 0.3)}`,
          }}>
            <Api className="api-icon" sx={{ fontSize: 18, transition: 'transform 0.3s ease' }} />
          </Box>
          <Typography variant="subtitle1" fontWeight="600" color={textColor}>
            {data.label || 'API Call'}
          </Typography>
        </Box>
      </Box>

      {/* Description */}
      {data.description && (
        <Typography
          variant="body2"
          sx={{
            mb: 2,
            color: alpha(textColor, 0.85),
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.4,
          }}
        >
          {data.description}
        </Typography>
      )}

      {/* API Fields */}
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
        {data.method && (
          <Chip
            label={data.method.toUpperCase()}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              fontWeight: 600,
              backgroundColor: alpha(getMethodColor(data.method), 0.2),
              color: getMethodColor(data.method),
              border: `1px solid ${alpha(getMethodColor(data.method), 0.4)}`,
            }}
          />
        )}
        {data.endpoint && (
          <Chip
            label={data.endpoint.length > 20 ? `${data.endpoint.substring(0, 20)}...` : data.endpoint}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              backgroundColor: alpha(theme.palette.warning.light, 0.2),
              color: theme.palette.warning.dark,
              border: `1px solid ${alpha(theme.palette.warning.light, 0.4)}`,
            }}
          />
        )}
        {data.headers && Object.keys(data.headers).length > 0 && (
          <Chip
            label={`${Object.keys(data.headers).length} headers`}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              backgroundColor: alpha(theme.palette.secondary.main, 0.2),
              color: theme.palette.secondary.dark,
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.4)}`,
            }}
          />
        )}
      </Box>

      {/* Status Indicator */}
      <Box
        sx={{
          position: 'absolute',
          top: -8,
          right: -8,
          width: 24,
          height: 24,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${nodeColor} 0%, #1976d2 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.2)}`,
          border: `2px solid ${theme.palette.background.paper}`,
        }}
      >
        <Api sx={{ fontSize: 14 }} />
      </Box>

      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: `linear-gradient(135deg, ${nodeColor} 0%, #1976d2 100%)`,
          border: `2px solid ${theme.palette.background.paper}`,
          width: 14,
          height: 14,
          boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.2)}`,
        }}
      />
    </Paper>
  );
});

APINode.displayName = 'APINode';

export default APINode;